import {
  ActiveFileFilterChip,
  DateRangeFilter,
  EnrichedProgrammingFile,
  ProgrammingFile,
  ProgrammingRowState
} from './programming-file';
import { getProgrammingFileStatusPillClass } from './programming-file-status';

interface ActiveFileFilterState {
  fileYear: string | null;
  fileStatus: string | null;
  searchValue: string;
  nameClient: string | null;
  startDateFileRange: DateRangeFilter;
}

export const formatFileDate = (
  date: string | number | Date | null | undefined
): string => {
  if (!date) return 'N/A';

  if (typeof date === 'string') {
    const trimmed = date.trim();
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return 'N/A';

  const day = parsedDate.getDate().toString().padStart(2, '0');
  const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}/${parsedDate.getFullYear()}`;
};

export const formatFileTime = (
  date: string | number | Date | null | undefined
): string => {
  if (!date) return '';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '';

  const hours = parsedDate.getHours().toString().padStart(2, '0');
  const minutes = parsedDate.getMinutes().toString().padStart(2, '0');
  const seconds = parsedDate.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

export const formatShortFileDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

export const enrichProgrammingFile = (
  file: ProgrammingFile
): EnrichedProgrammingFile => {
  const displayState = file.displayState ?? '';

  return {
    ...file,
    _paxAssigned: file.paxAssigned === true,
    _vuelosAssigned: file.flightsAssigned === true,
    _hasPartialFlights: file.hasPartialFlights === true,
    _paxCountDisplay: file.paxQuantity ?? 0,
    _tcCountDisplay: file.tcQuantity ?? 0,
    _rowBgColor: getProgrammingRowClass(file.rowState),
    _pillClass: getProgrammingFileStatusPillClass(displayState),
    _updatedAtDate: formatFileDate(file.updatedAt),
    _updatedAtTime: formatFileTime(file.updatedAt)
  };
};

export const toProgrammingFileListItem = (
  file: ProgrammingFile
): EnrichedProgrammingFile =>
  enrichProgrammingFile({
    ...file,
    fechaOperacion: file.fechaOperacion ?? file.startDateFile
  });

const getProgrammingRowClass = (
  rowState: ProgrammingRowState | null | undefined
): string => {
  if (rowState === 'LAST_MINUTE') return 'row-last-minute';
  if (rowState === 'REVIEW') return 'row-revisar';
  return '';
};

export const buildActiveFileFilterChips = (
  filters: ActiveFileFilterState
): ActiveFileFilterChip[] => {
  const chips: ActiveFileFilterChip[] = [];

  if (filters.fileYear) {
    chips.push({ id: 'year', label: `Año ${filters.fileYear}` });
  }
  if (filters.fileStatus) {
    chips.push({ id: 'status', label: filters.fileStatus });
  }
  if (filters.searchValue.trim()) {
    chips.push({
      id: 'search',
      label: `Búsqueda: ${filters.searchValue.trim()}`
    });
  }
  if (filters.nameClient) {
    chips.push({ id: 'nameClient', label: `Cliente: ${filters.nameClient}` });
  }
  if (filters.startDateFileRange.start && filters.startDateFileRange.end) {
    chips.push({
      id: 'startDateFile',
      label: `Operación: ${formatShortFileDate(filters.startDateFileRange.start)} - ${formatShortFileDate(filters.startDateFileRange.end)}`
    });
  }
  return chips;
};
